import { CognitoUserPool, CognitoUserAttribute, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import {
    CognitoIdentityProviderClient,
    AdminCreateUserCommand,
    AdminSetUserPasswordCommand
} from '@aws-sdk/client-cognito-identity-provider';
import { isNativePlatform } from '@/lib/platform';
import { Browser } from '@capacitor/browser';
import { getRuntimeEnvironment, isProductionRuntime } from '@/config/runtimeEnvironment';

/**
 * Custom URL scheme used as the OAuth redirect target when running inside the
 * native Capacitor app. Matches the `globethrivers://` entry already registered
 * in the Cognito user pool's Allowed callback URLs. Configured in iOS
 * Info.plist (CFBundleURLTypes) and Android AndroidManifest.xml (<intent-filter>).
 *
 * Cognito will redirect to this exact URL with `?code=...&state=...` appended,
 * which iOS/Android dispatch to our app via the `globethrivers` URL scheme.
 */
export const NATIVE_OAUTH_CALLBACK_SCHEME = 'globethrivers://';

/**
 * Begin an OAuth sign-in by sending the user to the Cognito Hosted UI.
 * On web: navigates the current tab. On native (Capacitor): opens the URL in
 * SFSafariViewController / Custom Tabs so the system browser can handle the
 * redirect back to our custom URL scheme. Without this, the WKWebView would
 * display the hosted UI in-app and would not be able to redirect to a
 * custom-scheme URL on completion.
 */
export async function startOAuthSignIn(url: string): Promise<void> {
    if (typeof window !== 'undefined' && isNativePlatform()) {
        await Browser.open({ url });
        return;
    }
    if (typeof window !== 'undefined') {
        window.location.href = url;
    }
}

/**
 * Resolve the OAuth redirect URI for the current platform.
 * Native: custom URL scheme. Web client: window.origin. SSR: env-driven defaults.
 */
export function getOAuthRedirectUri(): string {
    if (typeof window !== 'undefined' && isNativePlatform()) {
        return NATIVE_OAUTH_CALLBACK_SCHEME;
    }
    if (typeof window !== 'undefined') {
        return `${window.location.origin}/auth/callback`;
    }
    if (process.env.NEXT_PUBLIC_CALLBACK_URL) {
        return process.env.NEXT_PUBLIC_CALLBACK_URL;
    }
    const runtimeEnvironment = getRuntimeEnvironment();
    if (runtimeEnvironment === 'local') {
        return 'http://localhost:3001/auth/callback';
    }
    return runtimeEnvironment === 'production'
        ? 'https://app.globethrivers.com/auth/callback'
        : 'https://app-staging.globethrivers.com/auth/callback';
}

const trimOrUndefined = (value?: string) => (typeof value === 'string' ? value.trim() : undefined);

const withFallback = (value: string | undefined, fallback: string) => value ?? fallback;

const resolveCognitoConfig = () => {
    const isProd = isProductionRuntime();

    const defaults = isProd
        ? {
              userPoolId: 'eu-west-1_voNK6uLYL',
              clientId: '2e178ndqeakgu5s9hpe3j9g519',
              oauthDomain: 'globethrivers-prod.auth.eu-west-1.amazoncognito.com',
              region: 'eu-west-1'
          }
        : {
              userPoolId: 'eu-west-1_xNiQRfLkY',
              clientId: '1bg0effsljtc7rq340p59u1nrk',
              oauthDomain: 'globethrivers-dev.auth.eu-west-1.amazoncognito.com',
              region: 'eu-west-1'
          };

    return {
        userPoolId: withFallback(trimOrUndefined(process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID), defaults.userPoolId),
        userPoolClientId: withFallback(trimOrUndefined(process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID), defaults.clientId),
        oauthDomain: withFallback(trimOrUndefined(process.env.NEXT_PUBLIC_COGNITO_OAUTH_DOMAIN), defaults.oauthDomain),
        region: withFallback(trimOrUndefined(process.env.NEXT_PUBLIC_AWS_REGION), defaults.region)
    };
};

const cognitoConfig = resolveCognitoConfig();

const poolData = {
    UserPoolId: cognitoConfig.userPoolId,
    ClientId: cognitoConfig.userPoolClientId
};
const client = new CognitoIdentityProviderClient({ region: cognitoConfig.region });

export const userPool = new CognitoUserPool(poolData);

export interface SignUpResult {
    accessToken: string;
    idToken: string;
    refreshToken: string;
}

export async function signUpUser(fullData: {
    email: string;
    password: string;
    name?: string;
    dob?: string;
    city?: string;
}): Promise<SignUpResult> {
    return new Promise<SignUpResult>((resolve, reject) => {
        const attributeList = [
            new CognitoUserAttribute({ Name: 'name', Value: fullData.name || '' }),
            new CognitoUserAttribute({ Name: 'email', Value: fullData.email }),
            new CognitoUserAttribute({
                Name: 'birthdate',
                Value: fullData.dob || ''
            }),
            new CognitoUserAttribute({
                Name: 'address',
                Value: fullData.city || ''
            })
        ];

        userPool.signUp(fullData.email, fullData.password, attributeList, [], (err, _result) => {
            if (err) {
                reject(err);
            } else {
                const user = new CognitoUser({
                    Username: fullData.email,
                    Pool: userPool
                });
                const authDetails = new AuthenticationDetails({
                    Username: fullData.email,
                    Password: fullData.password
                });
                user.authenticateUser(authDetails, {
                    onSuccess: (session) => {
                        // Get tokens
                        const accessToken = session.getAccessToken().getJwtToken();
                        const idToken = session.getIdToken().getJwtToken();
                        const refreshToken = session.getRefreshToken().getToken();

                        resolve({ accessToken, idToken, refreshToken });
                    },
                    onFailure: (authErr: Error & { code?: string }) => {
                        // Handle UserNotConfirmedException - user needs email verification
                        if (authErr.code === 'UserNotConfirmedException') {
                            // Lambda should auto-verify, but if it didn't, retry after a short delay
                            setTimeout(() => {
                                user.authenticateUser(authDetails, {
                                    onSuccess: (session) => {
                                        const accessToken = session.getAccessToken().getJwtToken();
                                        const idToken = session.getIdToken().getJwtToken();
                                        const refreshToken = session.getRefreshToken().getToken();
                                        resolve({ accessToken, idToken, refreshToken });
                                    },
                                    onFailure: (retryErr) => {
                                        reject(retryErr);
                                    }
                                });
                            }, 1000);
                        } else {
                            reject(authErr);
                        }
                    }
                });
            }
        });
    });
}

export async function signUpUserNoConfirm(fullData: {
    email: string;
    password: string;
    name?: string;
    dob?: string;
    city?: string;
}) {
    const userPoolId = poolData.UserPoolId;

    await client.send(
        new AdminCreateUserCommand({
            UserPoolId: userPoolId,
            Username: fullData.email,
            UserAttributes: [
                { Name: 'email', Value: fullData.email },
                { Name: 'name', Value: fullData.name || '' },
                { Name: 'birthdate', Value: fullData.dob || '' },
                { Name: 'address', Value: fullData.city || '' },
                { Name: 'email_verified', Value: 'true' }
            ],
            MessageAction: 'SUPPRESS'
        })
    );

    await client.send(
        new AdminSetUserPasswordCommand({
            UserPoolId: userPoolId,
            Username: fullData.email,
            Password: fullData.password,
            Permanent: true
        })
    );

    return { success: true, email: fullData.email };
}

export interface LoginResult {
    accessToken: string;
    idToken: string;
    refreshToken: string;
}

export async function loginUser(email: string, password: string): Promise<LoginResult> {
    return new Promise<LoginResult>((resolve, reject) => {
        const user = new CognitoUser({ Username: email, Pool: userPool });
        const authDetails = new AuthenticationDetails({
            Username: email,
            Password: password
        });

        user.authenticateUser(authDetails, {
            onSuccess: (session) => {
                // you get tokens here
                resolve({
                    accessToken: session.getAccessToken().getJwtToken(),
                    idToken: session.getIdToken().getJwtToken(),
                    refreshToken: session.getRefreshToken().getToken()
                });
            },
            onFailure: (err) => {
                reject(err);
            }
        });
    });
}

export function getOAuthSignInUrl(provider: 'Google' | 'Apple', state?: string | null): string {
    const oauthDomain = cognitoConfig.oauthDomain;
    if (!oauthDomain) {
        throw new Error('OAuth domain not configured. Please set NEXT_PUBLIC_COGNITO_OAUTH_DOMAIN');
    }

    const redirectUri = getOAuthRedirectUri();

    const baseUrl = `https://${oauthDomain}`;
    const params = new URLSearchParams({
        identity_provider: provider,
        redirect_uri: redirectUri,
        response_type: 'code',
        client_id: poolData.ClientId,
        scope: 'email openid profile'
    });

    // Add state parameter if provided (for UTM tracking through OAuth flow)
    if (state) {
        params.set('state', state);
    }

    return `${baseUrl}/oauth2/authorize?${params.toString()}`;
}

export async function exchangeCodeForTokens(code: string) {
    const domain = cognitoConfig.oauthDomain;
    const clientId = poolData.ClientId;

    const redirectUri = getOAuthRedirectUri();

    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: redirectUri
    });

    const res = await fetch(`https://${domain}/oauth2/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
    });

    const responseText = await res.text();

    if (!res.ok) {
        throw new Error(`Failed to exchange code: ${res.status} ${responseText}`);
    }

    interface TokenResponse {
        access_token?: string;
        id_token?: string;
        refresh_token?: string;
        expires_in?: number;
        token_type?: string;
    }

    const tokens = JSON.parse(responseText) as TokenResponse;

    return {
        accessToken: tokens.access_token || '',
        idToken: tokens.id_token || '',
        refreshToken: tokens.refresh_token || '',
        expiresIn: tokens.expires_in,
        tokenType: tokens.token_type
    };
}

/**
 * Refreshes access and ID tokens using the raw refresh token stored in localStorage.
 * This is the fallback path when Amplify's fetchAuthSession has no session (e.g. OAuth
 * users who signed in via Google/Apple, where Amplify was never involved in the login).
 *
 * Both login flows store the refresh token in localStorage['refreshToken']:
 *   - Email/password: Login.tsx setAuthData()
 *   - OAuth (Google/Apple): CallbackPage exchangeCodeForTokens()
 *
 * Cognito does not return a new refresh token on this grant unless rotation is enabled.
 */
export type RefreshWithStoredTokenResult =
    | { ok: true; accessToken: string; idToken: string }
    | { ok: false; reason: 'missing' | 'rejected' | 'transient' };

export async function refreshWithStoredToken(): Promise<RefreshWithStoredTokenResult> {
    const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refreshToken') : null;
    if (!refreshToken) return { ok: false, reason: 'missing' };

    try {
        const response = await fetch(`https://${cognitoConfig.oauthDomain}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: poolData.ClientId,
                refresh_token: refreshToken
            }).toString()
        });

        if (!response.ok) {
            let rejected = response.status === 400 || response.status === 401;
            try {
                const body = await response.json() as { error?: string };
                if (body.error === 'invalid_grant') rejected = true;
            } catch {
                // ignore parse errors
            }
            return { ok: false, reason: rejected ? 'rejected' : 'transient' };
        }

        const data = await response.json() as { access_token?: string; id_token?: string };
        if (data.access_token && data.id_token) {
            localStorage.setItem('accessToken', data.access_token);
            localStorage.setItem('idToken', data.id_token);
            return { ok: true, accessToken: data.access_token, idToken: data.id_token };
        }
        return { ok: false, reason: 'transient' };
    } catch (_err) {
        return { ok: false, reason: 'transient' };
    }
}

// Password Reset Types
export interface CodeDeliveryDetails {
    DeliveryMedium: 'EMAIL' | 'SMS';
    Destination: string;
}

export interface ForgotPasswordSuccess {
    CodeDeliveryDetails: CodeDeliveryDetails;
}

export interface PasswordResetError extends Error {
    code: string;
    message: string;
    name: string;
}

export interface ConfirmPasswordSuccess {
    success: true;
}

// Password Reset Functions
export async function forgotPassword(email: string): Promise<ForgotPasswordSuccess> {
    return new Promise((resolve, reject) => {
        const user = new CognitoUser({ Username: email, Pool: userPool });
        
        user.forgotPassword({
            onSuccess: (data) => {
                resolve({
                    CodeDeliveryDetails: data.CodeDeliveryDetails
                });
            },
            onFailure: (err: Error) => {
                // Handle unverified email error
                const error = err as PasswordResetError;
                if (error.code === 'InvalidParameterException' && 
                    error.message?.includes('no registered/verified email')) {
                    const customError: PasswordResetError = {
                        ...error,
                        code: 'UNVERIFIED_EMAIL',
                        message: 'Email address is not verified. Please verify your email first.',
                        name: 'UnverifiedEmailError'
                    };
                    reject(customError);
                } else {
                    reject(error);
                }
            }
        });
    });
}

export async function confirmPassword(
    email: string, 
    verificationCode: string, 
    newPassword: string
): Promise<ConfirmPasswordSuccess> {
    return new Promise((resolve, reject) => {
        const user = new CognitoUser({ Username: email, Pool: userPool });
        
        user.confirmPassword(verificationCode, newPassword, {
            onSuccess: () => {
                resolve({ success: true });
            },
            onFailure: (err: Error) => {
                reject(err as PasswordResetError);
            }
        });
    });
}

export async function resendVerificationCode(email: string): Promise<CodeDeliveryDetails> {
    return new Promise((resolve, reject) => {
        const user = new CognitoUser({ Username: email, Pool: userPool });
        
        user.resendConfirmationCode((err, result) => {
            if (err) {
                reject(err);
            } else if (result) {
                resolve({
                    DeliveryMedium: result.CodeDeliveryDetails?.DeliveryMedium || 'EMAIL',
                    Destination: result.CodeDeliveryDetails?.Destination || ''
                });
            } else {
                reject(new Error('Failed to resend verification code'));
            }
        });
    });
}
